import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * A badge component that displays a numeric count.
 * If the count exceeds the threshold, it shows "{threshold}+" instead.
 *
 * Usage:
 * <nx-count-badge [count]="42" />
 * <nx-count-badge [count]="1500" [threshold]="1000" />
 */
@Component({
  selector: 'nx-count-badge',
  template: `<span [class]="badgeClass()">{{ displayValue() }}</span>`,
  styles: [`:host { display: inline-flex !important; vertical-align: middle !important; align-items: center !important; }`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountBadgeComponent {

  /**
   * The numeric count to display.
   */
  readonly count = input.required<number>();

  /**
   * The threshold above which "{threshold}+" is shown instead of the exact count.
   * @default 1000
   */
  readonly threshold = input<number>(1000);

  /**
   * Additional CSS classes to apply to the badge span.
   */
  readonly styleClass = input<string>('');

  /**
   * The value shown in the badge – either the count or "{threshold}+".
   */
  readonly displayValue = () =>
    this.count() > this.threshold()
      ? `${this.threshold()}+`
      : this.count();

  /**
   * Combined badge class.
   */
  readonly badgeClass = () =>
    `ml-2 px-3 py-1 rounded-full text-sm font-semibold bg-white text-black ${this.styleClass()}`.trim();
}
